import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/de'
import 'dayjs/locale/en'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)

let appTimezone = 'UTC'
let appLocale = 'en'

const DATE_FORMATS = {
  US: 'MM/DD/YYYY HH:mm',
  DE: 'DD.MM.YYYY HH:mm',
}

const DATE_TIME_WITH_SECONDS_FORMATS = {
  US: 'MM/DD/YYYY HH:mm:ss',
  DE: 'DD.MM.YYYY HH:mm:ss',
}

const normalizeTimezone = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return 'UTC'
  }

  return value.trim()
}

const normalizeLocale = (value) => {
  return value === 'de' ? 'de' : 'en'
}

const parseInAppTimezone = (value) => {
  return dayjs(value).tz(appTimezone)
}

export const setDateTimeTimezone = (timezoneValue) => {
  appTimezone = normalizeTimezone(timezoneValue)
  dayjs.tz.setDefault(appTimezone)
}

export const setDateTimeLocale = (localeValue) => {
  appLocale = normalizeLocale(localeValue)
  dayjs.locale(appLocale)
}

export const getDateTimeTimezone = () => {
  return appTimezone
}

export const getDateTimeLocale = () => {
  return appLocale
}

export const nowInAppTimezone = () => {
  return dayjs().tz(appTimezone)
}

export const toAppTimezone = (value) => {
  if (!value) {
    return null
  }

  return parseInAppTimezone(value)
}

export const formatDateTime = (value, { locale = 'en', dateFormat = 'US', withSeconds = false } = {}) => {
  if (!value) {
    return null
  }

  const formats = withSeconds ? DATE_TIME_WITH_SECONDS_FORMATS : DATE_FORMATS
  const format = formats[dateFormat] ?? formats.US

  return parseInAppTimezone(value)
    .locale(normalizeLocale(locale))
    .format(format)
}

export const formatDate = (value, options = {}) => {
  return formatDateTime(value, {
    ...options,
    withSeconds: false,
  })
}

export const formatDateWithSeconds = (value, options = {}) => {
  return formatDateTime(value, {
    ...options,
    withSeconds: true,
  })
}

export const formatDateWithTimezone = (value, { locale = 'en', dateFormat = 'US', fallback = '-' } = {}) => {
  const formattedDateTime = formatDateWithSeconds(value, {
    locale,
    dateFormat,
  })

  if (!formattedDateTime) {
    return fallback
  }

  return `${formattedDateTime} (${getDateTimeTimezone()})`
}

export const formatRelativeTimeFromNow = (value, { locale = 'en', fallback = '-' } = {}) => {
  if (!value) {
    return fallback
  }

  return parseInAppTimezone(value)
    .locale(normalizeLocale(locale))
    .from(nowInAppTimezone())
}

export const formatHumanReadableDateTime = (
  value,
  {
    locale = appLocale,
    dateFormat = 'US',
    fallback = '-',
    relativeThresholdMinutes = 1440,
    forceAbsolute = false,
  } = {},
) => {
  if (!value) {
    return fallback
  }

  const parsedValue = parseInAppTimezone(value)
  if (!parsedValue.isValid()) {
    return fallback
  }

  if (!forceAbsolute) {
    const diffInMinutes = Math.abs(parsedValue.diff(nowInAppTimezone(), 'minute', true))
    if (diffInMinutes < relativeThresholdMinutes) {
      return parsedValue
        .locale(normalizeLocale(locale))
        .from(nowInAppTimezone())
    }
  }

  const format = DATE_FORMATS[dateFormat] ?? DATE_FORMATS.US

  return parsedValue
    .locale(normalizeLocale(locale))
    .format(format)
}
